import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChatMessagesComponent } from '../../../components/communication/chats/chat-messages/chat-messages.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StudentApiServiceService } from '../../personnel/student/student-api-service.service';
import { StudentCachedService } from '../../../services/student-cached.service';

import { Store } from '@ngrx/store';
import { selectUser } from '../../../auth/store/auth.selector';
import { environment } from '../../../../environments/environment';
import { FirebaseService, FirebaseChatSessionSummary } from '../../../components/communication/chats/firebase.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-chats',
  standalone: true,
  imports: [ChatMessagesComponent, CommonModule, FormsModule],
  templateUrl: './chats.component.html',
  styleUrl: './chats.component.css',
})
export class ChatsComponent implements OnDestroy {
  private mediaBaseUrl = environment.mediaBaseUrl;
  readonly defaultProfileImage = 'https://media.istockphoto.com/id/1495088043/vector/user-profile-icon-avatar-or-person-icon-profile-picture-portrait-symbol-default-portrait.jpg?s=612x612&w=0&k=20&c=dhV2p1JwmloBTOaGAtaA3AW1KSnjsdMt7-U_3EZElZ0=';
  searchtext: string = '';
  selectedChat: any = null;
  showChatOnMobile = false; // Track if chat is open on mobile
  currentUserId: number | null = null;
  currentUserType: string = 'teacher';
  isLoading: boolean = true;

  chatList: any[] = [];
  filterdchatllist = [...this.chatList];
  private chatSummarySubscription: Subscription = new Subscription();
  private unseenMessagesSubscription: Subscription = new Subscription();
  private sessionSummaries: Record<string, FirebaseChatSessionSummary> = {};
  private unseenCounts: Record<string, number> = {};
  private pendingChatId: string | null = null;

constructor(
    private studentApi: StudentApiServiceService,
    private studentCached: StudentCachedService,
    private store: Store,
    private firebaseService: FirebaseService,
    private route: ActivatedRoute
  ) {
    this.store.select(selectUser).subscribe((user: any) => {
      this.currentUserId = user?.id ?? null;
      const roleName = (user?.role?.name || user?.role_name || '').toString().toLowerCase();
      this.currentUserType = roleName === 'client' ? 'client' : 'teacher';
      this.refreshChatContext();
      this.loadChatSessionSummaries();
      this.listenToUnseenMessages();
    });

    this.listenToRouteParams();
    this.loadStudents();
  }


  private listenToRouteParams() {
    this.route.queryParams.subscribe((params) => {
      const type = (params['type'] ?? '').toString().toLowerCase();
      const id = params['id'];

      // Only auto-open chat for students.
      if (type !== 'student') {
        this.pendingChatId = null;
        this.showChatOnMobile = false;
        this.selectedChat = null;
        return;
      }

      if (id == null || id === '') {
        this.pendingChatId = null;
        return;
      }

      // Store the pending chat ID
      this.pendingChatId = id.toString();



      // If chat list already loaded, select immediately.
      const exists = this.chatList?.some((c) => c?.id?.toString() === id?.toString());
      if (exists) {
        this.selectChat(id);
        this.showChatOnMobile = true;
        this.pendingChatId = null;
        return;
      }

      // Not found in current chat list; keep pendingChatId until students load.


      // Otherwise, try again after students list is mapped.
      // setChatListFromStudents() calls applySessionSummaries() which calls filterChats().
      setTimeout(() => {
        const existsAfterLoad = this.chatList?.some((c) => c?.id?.toString() === id?.toString());
        if (existsAfterLoad) {
          this.selectChat(id);
          this.showChatOnMobile = true;
          this.pendingChatId = null;
        }
      }, 300);
    });
  }

  // Method to open chat on mobile
  openChat(chatId: any) {
    this.selectChat(chatId);
    this.showChatOnMobile = true;
  }

  selectChat(chatId: any) {
    this.selectedChat = chatId?.toString();
    this.clearUnreadState(chatId);
    
    // Trigger change detection for the chat-messages component
    setTimeout(() => {
      this.filterChats();
    }, 0);
  }

  clear() {
    this.searchtext = '';
    this.filterChats();
  }

  // Method to go back to chat list on mobile
  backToChatList() {
    this.showChatOnMobile = false;
  }

  // Get selected chat details for mobile header
  getSelectedChat() {
    return this.chatList.find((chat) => chat.id?.toString() === this.selectedChat?.toString());
  }

  private loadStudents() {
    this.isLoading = true;
    this.chatList = [];
    this.filterdchatllist = [];

    // Fetch ALL students in a single request using: student/get-all?all=true
    this.studentApi.getStudentList(undefined, { all: true }).subscribe({
      next: (res: any) => {
        const students = Array.isArray(res?.data) ? res.data : Array.isArray( res) ? res : [];
        this.setChatListFromStudents(students);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load students:', err);
        this.isLoading = false;
      }
    });
  }


  private setChatListFromStudents(students: any[]) {
    this.chatList = students.map((student: any) => ({
      id: student?.id,
      studentId: student?.id,
      userId: this.currentUserId,
      userType: this.currentUserType,
      name: `${student?.first_name ?? ''} ${student?.last_name ?? ''}`.trim() || student?.name || 'Student',
      class: student?.class_name || student?.class || '',
      profile: student?.profile_image ? `${student.profile_image}` : null,
      message: '',
      time: '',
      lastactive: student?.is_active ? 'Online' : 'Offline',
      unreadCount: 0
    }));
    this.applySessionSummaries();
    
    // After chat list is loaded, check if there's a pending chat ID from route params
    if (this.pendingChatId) {
      const exists = this.chatList?.some((c) => c?.id?.toString() === this.pendingChatId);
      if (exists) {
        this.selectChat(this.pendingChatId);
        this.showChatOnMobile = true;
        this.pendingChatId = null;
      }
    }
  }

  private loadChatSessionSummaries() {
    this.chatSummarySubscription.unsubscribe();
    if (!this.currentUserId) {
      return;
    }

    this.chatSummarySubscription = this.firebaseService.listenToChatSessionsForUser(this.currentUserId.toString()).subscribe({
      next: (summaries) => {
        this.sessionSummaries = summaries.reduce((acc, summary) => {
          acc[summary.otherUserId] = summary;
          return acc;
        }, {} as Record<string, FirebaseChatSessionSummary>);
        this.applySessionSummaries();
      },
      error: (err) => {
        console.error('Failed to load chat summaries:', err);
      }
    });
  }

  private applySessionSummaries() {
    this.chatList = this.chatList.map((chat) => {
      const summary = this.sessionSummaries[chat.studentId?.toString()];
      const isSelectedChat = chat.id?.toString() === this.selectedChat?.toString();
      const unreadCount = isSelectedChat ? 0 : this.unseenCounts[chat.studentId?.toString()] ?? 0;
      const hasUnseen = unreadCount > 0;
      return {
        ...chat,
        message: summary?.lastMessage ?? chat.message,
        time: summary?.lastMessageAt ? new Date(summary.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : chat.time,
        lastactive: summary?.lastMessageAt ? new Date(summary.lastMessageAt).toISOString() : chat.lastactive,
        unreadCount,
        hasUnseen,
        summary
      };
    });

    this.sortChatList();
    this.filterChats();
  }

  private listenToUnseenMessages() {
    this.unseenMessagesSubscription.unsubscribe();
    if (!this.currentUserId) return;

    const currentUserId = this.currentUserId.toString();
    this.unseenMessagesSubscription = this.firebaseService.listenToUnseenMessagesForUser(currentUserId).subscribe({
      next: (unseenByChat: Record<string, number>) => {
        this.unseenCounts = unseenByChat;
        this.applySessionSummaries();
      },
      error: (err) => {
        console.error('Failed to listen to unseen messages:', err);
      }
    });
  }

  private sortChatList() {
    this.chatList.sort((a, b) => {
      const first = a.summary?.lastMessageAt ?? 0;
      const second = b.summary?.lastMessageAt ?? 0;
      if (first === second) {
        return (a.name || '').localeCompare(b.name || '');
      }
      return second - first;
    });
    this.filterdchatllist = [...this.chatList];
  }

  private refreshChatContext() {
    if (!this.chatList.length) return;
    this.chatList = this.chatList.map(chat => ({
      ...chat,
      userId: this.currentUserId,
      userType: this.currentUserType
    }));
    this.filterdchatllist = [...this.chatList];
  }

  private clearUnreadState(chatId: any) {
    const chat = this.chatList.find((item) => item.id?.toString() === chatId?.toString());
    const studentId = chat?.studentId?.toString();
    if (!studentId || !this.unseenCounts[studentId]) {
      return;
    }

    this.unseenCounts = {
      ...this.unseenCounts,
      [studentId]: 0
    };
    this.applySessionSummaries();
  }

  filterChats() {
    if(!this.searchtext) {
      this.filterdchatllist = [...this.chatList];
      return;
    }
    const text = this.searchtext.toLowerCase();

    this.filterdchatllist = this.chatList.filter(
      (chat) =>
        chat.name.toLowerCase().includes(text) ||
        (chat.message || '').toLowerCase().includes(text)
    );
  }

  ngOnDestroy() {
    this.chatSummarySubscription.unsubscribe();
    this.unseenMessagesSubscription.unsubscribe();
  }
}
